import { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Editor } from "primereact/editor";
import { useNavigate, useParams } from "react-router-dom";
import CustomInput from "../components/CustomInput";
import { AuthContext } from "../../../contexts/AuthContext";
import Loading from "../components/Loading";
import { categoryService } from "../../../services/api/categoryService";
import { firebaseService } from "../../../services/api/firebaseService";
import { formatDate } from "../../../utils/DateFormat";

const formatData = (item) => ({
  createDate: formatDate(item.createdAt || new Date().toISOString()),
  name: item.name || "Chưa có tên",
  detail: item.description || "Không có mô tả",
  image: item.image || "/images/logo.png",
  modifiedDate: formatDate(item.updatedAt || new Date().toISOString()),
  nav_id: item.id || null,
});

const EditCategory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [imgSrc, setImgSrc] = useState("");
  const [imgFile, setImgFile] = useState(null);

  const [oldImageUrl, setOldImageUrl] = useState(""); // Old logo
  const [oldContentImages, setOldContentImages] = useState([]); // Images in content

  /** Extract Firebase image URLs from HTML content */
  const extractFirebaseImageUrls = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");
    const firebaseUrls = [];
    images.forEach((img) => {
      const src = img.getAttribute("src");
      if (src && (src.startsWith("https://storage.googleapis.com/") || src.startsWith("https://firebasestorage.googleapis.com/"))) {
        firebaseUrls.push(src);
      }
    });
    return firebaseUrls;
  };

  /** Process images in HTML content and upload to Firebase */
  const processImagesInContent = async (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string" || htmlContent === "<p><br></p>") {
      return htmlContent || "";
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");
    if (images.length === 0) return htmlContent;

    for (const img of images) {
      const src = img.getAttribute("src");
      if (!src || src.startsWith("https://storage.googleapis.com/") || src.startsWith("https://firebasestorage.googleapis.com/")) {
        continue;
      }
      if (src.startsWith("data:image/")) {
        try {
          const fetchRes = await fetch(src);
          const blob = await fetchRes.blob();
          const fileName = `category-editor-${Date.now()}.png`;
          const file = new File([blob], fileName, { type: blob.type });
          const uploadRes = await firebaseService.upload_file(token, file, "category-content");
          const uploadedUrl = uploadRes?.url;
          if (!uploadedUrl) throw new Error("Upload ảnh thất bại");
          img.setAttribute("src", uploadedUrl);
        } catch (error) {
          console.error("Error uploading image:", error);
          toast.error(`Không thể upload ảnh trong nội dung: ${error.message}`);
          throw error;
        }
      }
    }
    return doc.body.innerHTML;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Chỉ chấp nhận định dạng JPEG/PNG/WEBP.");
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error("Kích thước tệp vượt quá 25MB.");
      return;
    }
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImgSrc(reader.result || "/images/avatar-default.png");
      };
      reader.onerror = () => {
        toast.error("Không thể đọc tệp. Vui lòng thử lại.");
      };
      reader.readAsDataURL(file);
      setImgFile(file);
    }
  };

  const handleInputChange = (key, value) => {
    setData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await categoryService.get_by_id(id, token);
      setData(formatData(response));
      setImgSrc(response.image || "/images/logo.png");
      setOldImageUrl(response.image || "");
      setOldContentImages(extractFirebaseImageUrls(response.description || ""));
    } catch (error) {
      toast.dismiss();
      toast.error(
        error.response
          ? `${error.response?.data?.message}`
          : "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const validateFields = () => {
    let tempErrors = {};
    if (!data.name.trim()) tempErrors.name = "Tên loại dịch vụ không được để trống.";
    if (!data.detail || data.detail.trim() === "<p><br></p>") tempErrors.detail = "Nội dung không được để trống.";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleEdit = async () => {
    if (!validateFields()) return;
    try {
      setLoading(true);

      // 1. Upload logo if changed
      let finalImageUrl = imgSrc;
      if (imgFile) {
        const uploadRes = await firebaseService.upload_file(
          token,
          imgFile,
          "category-images",
        );
        if (!uploadRes?.url) {
          toast.error("Upload hình ảnh thất bại");
          return;
        }
        finalImageUrl = uploadRes.url;
      }

      // 2. Process images in content
      const processedDetail = await processImagesInContent(data.detail);
      const newContentImages = extractFirebaseImageUrls(processedDetail);

      // 3. Prepare payload
      const payload = {
        categoryName: data.name,
        description: processedDetail,
        image: finalImageUrl,
      };

      // 4. Update in database
      await categoryService.update_category(id, payload, token);

      // 5. Cleanup old logo
      if (
        imgFile &&
        oldImageUrl &&
        (oldImageUrl.startsWith("https://storage.googleapis.com/") || oldImageUrl.startsWith("https://firebasestorage.googleapis.com/"))
      ) {
        try {
          await firebaseService.delete_file(token, oldImageUrl);
        } catch (err) {
          console.warn("Failed to delete old logo:", err);
        }
      }

      // 6. Cleanup old images in content
      if (oldContentImages.length > 0) {
        const imagesToDelete = oldContentImages.filter(url => !newContentImages.includes(url));
        for (const url of imagesToDelete) {
          try {
            await firebaseService.delete_file(token, url);
          } catch (err) {
            console.warn("Failed to delete orphaned image:", url, err);
          }
        }
      }

      toast.success("Cập nhật loại dịch vụ thành công!");
      navigate("/loai-dich-vu");
    } catch (error) {
      toast.dismiss();
      toast.error(
        error.response
          ? `${error.response?.data?.message}`
          : "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (
      window.confirm(
        "Bạn có chắc chắn muốn hủy? Các thay đổi sẽ không được lưu.",
      )
    ) {
      navigate("/loai-dich-vu");
    }
  };

  if (loading)
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );

  if (!data) return <div>Không có dữ liệu để hiển thị.</div>;
  return (
    <div className="bg-white rounded-lg p-6 overflow-auto border-b h-full">
      <h1 className="text-center font-sf-semibold text-[#0457C2] text-2xl uppercase border-b p-4">
        Chỉnh sửa loại dịch vụ {data.name}
      </h1>

      <div className="px-[5vw] mt-6 flex flex-col justify-center">
        <strong className="w-52">Hình ảnh loại dịch vụ:</strong>
        <label
          htmlFor="image-upload"
          className="cursor-pointer w-28 h-28 bg-gray-100 flex items-center justify-center rounded-md overflow-hidden relative hover:opacity-75 border-2 border-dashed border-gray-300"
        >
          {data.image ? (
            <img
              src={imgSrc}
              alt="Uploaded"
              className="w-full h-full object-cover object-center shrink-0"
            />
          ) : (
            <div className="flex items-center justify-center text-gray-400">
              <img src="/icons/camera-icon.svg" alt="Camera icon" />
            </div>
          )}
          <input
            id="image-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageChange}
          />
        </label>
      </div>

      <div className="flex items-center justify-between px-[5vw] pt-6">
        <div className="flex flex-col gap-2 items-start mb-4 w-full">
          <strong className="w-52 font-bold text-gray-700">Tên loại dịch vụ:</strong>
          <CustomInput
            value={data.name}
            placeholder="Nhập tên loại dịch vụ"
            required={true}
            onChange={(value) => handleInputChange("name", value)}
            error={errors.name}
          />
        </div>
      </div>

      <div className="px-[5vw] pb-6 flex flex-col gap-2">
        <strong className="font-bold text-gray-700">Nội dung mô tả:</strong>
        <Editor
          value={data.detail}
          onTextChange={(e) => handleInputChange("detail", e.htmlValue)}
          style={{ minHeight: "350px" }}
          placeholder="Nhập miêu tả loại dịch vụ..."
        />
        {errors.detail && <p className="text-red-500 text-sm mt-1">{errors.detail}</p>}
      </div>

      <div className="flex justify-end space-x-4 px-[5vw] pb-10">
        <button
          type="button"
          onClick={handleCancel}
          className="px-6 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 flex gap-2 font-medium"
        >
          Hủy
        </button>
        <button
          onClick={handleEdit}
          className="bg-blue-600 text-white border rounded-lg px-8 py-2 flex items-center gap-2 hover:bg-blue-700 font-semibold shadow-md"
        >
          Lưu thay đổi
        </button>
      </div>
    </div>
  );
};

export default EditCategory;
